import { inject, Injectable } from '@angular/core';
import { MissingTranslationHandler, MissingTranslationHandlerParams, StrictTranslation } from '@ngx-translate/core';
import { ENVIRONMENT, EnvironmentConfig, MISSING_KEY_PREFIX } from '@lib/rs-ngx';
import * as Sentry from '@sentry/angular';

/**
 * **IMPORTANT:** This custom handler is a prototype intended to test how to log missing translation
 * key values. Do not replicate or use it as an example for other implementations.
 *
 * Custom handler for missing translation keys in ngx-translate.
 *
 * Behavior:
 * - Logs missing keys to Sentry as warnings (tagged with `translation.missing-key`)
 *   so the team can track and fix them. Each key is reported only once per session
 *   to avoid flooding Sentry.
 *
 * Special cases:
 * - `ifEmpty` interpolation param: when a translation pipe passes `{ ifEmpty: value }`,
 *   the handler returns that value instead of the "Missing Key" prefix.
 *   Usage: `{{ 'KEY' | translate: { ifEmpty: 'fallback' } }}`
 *
 * - Keys ending in `.null` or `.undefined`: these are caused by template expressions
 *   like `{{ 'PREFIX.' + nullableValue | translate }}` where the value is null/undefined.
 *   These keys are silently ignored (no Sentry report) since they are not real missing
 *   translations but a data issue upstream.
 *
 */
@Injectable()
export class OrdMissingTranslationHandler implements MissingTranslationHandler {
  private readonly NULL_UNDEFINED_KEY = /\.(null|undefined)$/i;
  private readonly reportedKeys = new Set<string>();
  private readonly envConfig: EnvironmentConfig = inject(ENVIRONMENT);

  public handle( params: MissingTranslationHandlerParams ): StrictTranslation {
    if (params.interpolateParams && 'ifEmpty' in params.interpolateParams) {
      return params.interpolateParams['ifEmpty'] as StrictTranslation;
    }

    const missingKeyMsg = MISSING_KEY_PREFIX + params.key;
    this.reportMissingKey(params.key, missingKeyMsg);
    return missingKeyMsg;
  }

  private reportMissingKey( key: string, message: string ): void {
    if (this.reportedKeys.has(key) || this.NULL_UNDEFINED_KEY.test(key)) {
      return;
    }

    this.reportedKeys.add(key);

    if (this.envConfig.environment === 'development') {
      console.error(message);
    }

    Sentry.captureMessage(message, scope =>
      scope
        .setTag('translation.missing-key', key)
        .setLevel('warning')
        .setExtra('translationKey', key)
    );

  }
}

export const provideMissingTranslationHandler = <T extends new ( ...args: unknown[] ) => MissingTranslationHandler>( handler: T ) => {
  return {
    provide: MissingTranslationHandler,
    useClass: handler
  };
};
